import React from 'react';
import { Delete, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  styled,
} from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'further-ui/components/Accordion';
import ErrorBoundary from 'components/ErrorBoundary';
import { AssetHistoryType } from 'further-types/lp/asset';
import DatePicker from 'further-ui/components/DatePicker';
import { Column, Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import {
  CalendarDay,
  calculateLcyValue,
  getCurrencySymbol,
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { revaluation as rules } from 'further-ui/validations';
import { maxBy, sumBy } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import { AssetFullHistoryItem } from '../FormWrapper';
import useRevaluationsFormHandlers from '../hooks/useRevaluationFormHandlers';
import useRevaluations from '../hooks/useRevaluations';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';

const typeToLabel = (type: AssetHistoryType) => {
  switch (type) {
    case AssetHistoryType.Investment:
      return 'Investment';
    case AssetHistoryType.Revaluation:
      return 'Revaluation';
    case AssetHistoryType.Realisation:
      return 'Realisation';
    default:
      return '';
  }
};

const ValueRow = styled(Row)(({ theme }) => ({
  paddingRight: theme.spacing(theme.spacingSizes.sm),
}));

const TransparentTableWrapper = styled('div')(({ theme }) => ({
  '& .MuiTableContainer-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: 'transparent',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.common.white,
  },
}));

const BorderlessAccordion = styled(Accordion)({
  boxShadow: 'none',
  '&::before': { display: 'none' },
});

const StackedDivider = styled(Divider)({
  position: 'relative',
});

const PaddedValue = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'padded',
})<{ padded?: boolean }>(({ theme, padded }) => ({
  paddingLeft: padded ? theme.spacing(2.5) : undefined,
}));

type IndividualRevaluationProps = {
  assetId: string;
  vehicleId: string;
  viewOnly?: boolean;
  disableBorder?: boolean;
  simpleHeader?: boolean;
  isNonLcy?: boolean;
  vehicleBaseCurrency?: CurrencyCode;
  fxRate?: CurrencyRateDetails | null;
  disableDate?: boolean;
  assetFullHistory: Record<string, Array<AssetFullHistoryItem>>;
};

const HoldingClassRevaluationSection: React.FC<IndividualRevaluationProps> = ({
  viewOnly,
  disableBorder,
  simpleHeader,
  isNonLcy,
  vehicleBaseCurrency,
  fxRate,
  disableDate,
  assetFullHistory,
}) => {
  const { watch, control, getValues, setValue } =
    useFormContext<rules.RevaluationFields>();

  const {
    getInvestmentsPerHoldingClass,
    calculateUnitPricePerHoldingClass,
    deleteNewRevaluation,
    addNewRevaluation,
    shouldRenderNewRevaluation,
    calculateAssetInitialHoldValue,
    findRevaluationFieldIndex,
  } = useRevaluations({
    watch,
    control,
    getValues,
  });

  const { handleCurrentHoldValueChange, handleUnitPriceChange, setFieldFocus } =
    useRevaluationsFormHandlers({
      watch,
      setValue,
    });

  const getRevaluationsPerHoldingClass = (holdingClassId: string) => {
    return assetFullHistory?.[holdingClassId] ?? [];
  };

  const AccordionComponent = disableBorder ? BorderlessAccordion : Accordion;

  return (
    <ErrorBoundary>
      <Column>
        {Object.entries(assetFullHistory ?? {}).map(
          ([holdingClassId, group], index, entries) => {
            const holdingClassInvestments =
              getInvestmentsPerHoldingClass(holdingClassId);

            const holdingClassName = group[0]?.holdingClassName;

            const currentNumberOfUnits = sumBy(
              holdingClassInvestments,
              (investment) => investment.currentNumberOfUnits,
            );

            const currency = group[0]?.currency;

            const currentUnitPrice =
              calculateUnitPricePerHoldingClass(holdingClassId);

            const currentHoldValue =
              currentNumberOfUnits && currentUnitPrice !== undefined
                ? currentNumberOfUnits * currentUnitPrice
                : sumBy(
                    holdingClassInvestments,
                    (investment) => investment.currentUnrealisedValue,
                  );

            const latestRevaluation = maxBy(
              getRevaluationsPerHoldingClass(holdingClassId),
              (historyItem) => historyItem.ledgerDate,
            );

            const { index: revaluationIndex } = findRevaluationFieldIndex(
              holdingClassId,
              holdingClassId,
              'newRevaluations',
            );

            const safeIndex = revaluationIndex ?? index;

            const newHoldValueTcy =
              watch(`newRevaluations.${safeIndex}.currentHoldValue`) ?? 0;
            const newHoldValueLcy = calculateLcyValue(newHoldValueTcy, fxRate);

            const rows = group.map((historyItem, historyIndex) => ({
              id: `${holdingClassId}-${historyIndex}`,
              stage: typeToLabel(historyItem.type),
              totalHoldValue:
                historyItem.unitPriceTcy * historyItem.currentNumberOfUnits,
              totalHoldValueLcy:
                historyItem.unitPriceLcy != null
                  ? historyItem.unitPriceLcy * historyItem.currentNumberOfUnits
                  : null,
              averageUnitPrice: historyItem.unitPriceTcy,
              unitsHeld: historyItem.currentNumberOfUnits,
              revaluationDate: new CalendarDay(historyItem.ledgerDate),
              currency: historyItem.currency,
              isNewRevaluation: false,
            }));

            if (shouldRenderNewRevaluation(holdingClassId)) {
              rows.push({
                id: `${holdingClassId}-new`,
                stage: 'New revaluation',
                totalHoldValue: 0,
                totalHoldValueLcy: newHoldValueLcy,
                averageUnitPrice: 0,
                unitsHeld: currentNumberOfUnits,
                revaluationDate: new CalendarDay(new Date()),
                currency,
                isNewRevaluation: true,
              });
            }

            const hasNewRevaluation = rows.some((r) => r.isNewRevaluation);

            type HistoryItem = (typeof rows)[number];

            const columns: Array<TableColumn<HistoryItem>> = [
              {
                label: 'Stage',
                key: 'stage',
                width: 200,
                render: ({ stage }) => <ColorIndicatorWithText text={stage} />,
              },
              {
                label:
                  isNonLcy && currency
                    ? `Total hold value (${getCurrencySymbol(currency)})`
                    : 'Total hold value',
                key: 'totalHoldValue',
                width: 150,
                render: (row) =>
                  row.isNewRevaluation ? (
                    <Controller
                      name={`newRevaluations.${safeIndex}.currentHoldValue`}
                      control={control}
                      defaultValue={calculateAssetInitialHoldValue(
                        currentNumberOfUnits,
                        safeIndex,
                        'newRevaluations',
                      )}
                      render={({
                        field: { ref, onChange, ...rest },
                        fieldState: { error },
                      }) => (
                        <NumberInput
                          {...rest}
                          onFocus={() => setFieldFocus('holdValue')}
                          disabled={viewOnly}
                          onChange={(value) => {
                            onChange(value ?? 0);

                            handleCurrentHoldValueChange(
                              value ?? 0,
                              currentNumberOfUnits ?? 0,
                              safeIndex,
                              'newRevaluations',
                            );
                          }}
                          currency={currency}
                          decimals={2}
                          fixedDecimalScale
                          error={!!error}
                          helperText={error?.message}
                          size="small"
                        />
                      )}
                    />
                  ) : (
                    <PaddedValue padded={hasNewRevaluation}>
                      {numberToCurrencyString(row.totalHoldValue, {
                        currency: row.currency,
                      })}
                    </PaddedValue>
                  ),
              },
              ...(isNonLcy && vehicleBaseCurrency
                ? [
                    {
                      label: `Total hold value (${getCurrencySymbol(
                        vehicleBaseCurrency,
                      )})`,
                      key: 'totalHoldValueLcy' as const,
                      width: 180,
                      render: (row: HistoryItem) =>
                        row.totalHoldValueLcy == null
                          ? '--'
                          : numberToCurrencyString(row.totalHoldValueLcy, {
                              currency: vehicleBaseCurrency,
                            }),
                    },
                  ]
                : []),
              {
                label: 'Average unit price',
                key: 'averageUnitPrice',
                width: 160,
                render: (row) =>
                  row.isNewRevaluation ? (
                    <Controller
                      name={`newRevaluations.${safeIndex}.unitPriceTcy`}
                      control={control}
                      render={({
                        field: { ref, onChange, ...rest },
                        fieldState: { error },
                      }) => (
                        <NumberInput
                          {...rest}
                          fullWidth
                          onFocus={() => setFieldFocus('unitPriceTcy')}
                          disabled={viewOnly}
                          onChange={(value) => {
                            onChange(value ?? 0);

                            handleUnitPriceChange(
                              value ?? 0,
                              currentNumberOfUnits ?? 0,
                              safeIndex,
                              'newRevaluations',
                            );
                          }}
                          currency={currency}
                          decimals={2}
                          maxDecimals={10}
                          fixedDecimalScale
                          error={!!error}
                          helperText={error?.message}
                          size="small"
                        />
                      )}
                    />
                  ) : (
                    <PaddedValue padded={hasNewRevaluation}>
                      {numberToCurrencyString(row.averageUnitPrice, {
                        currency: row.currency,
                      })}
                    </PaddedValue>
                  ),
              },
              {
                label: 'Units held',
                key: 'unitsHeld',
                width: 150,
                render: (row) => numberToDisplayString(row.unitsHeld),
              },
              {
                label: 'Revaluation date',
                key: 'revaluationDate',
                width: 175,
                render: (row) =>
                  row.isNewRevaluation ? (
                    <Controller
                      name={`newRevaluations.${safeIndex}.ledgerDate`}
                      control={control}
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => (
                        <DatePicker
                          value={new CalendarDay(value ?? new Date())}
                          onChange={(date) => {
                            onChange(date?.toUTCDate());
                          }}
                          disabled={viewOnly || disableDate}
                          error={!!error}
                          fullWidth
                          helperText={error?.message}
                          size="small"
                          minDate={latestRevaluation?.ledgerDate}
                          disableFuture
                        />
                      )}
                    />
                  ) : (
                    <PaddedValue padded={hasNewRevaluation}>
                      {row.revaluationDate.toReadable()}
                    </PaddedValue>
                  ),
              },
              {
                label: '',
                key: 'remove',
                width: 50,
                render: (row) =>
                  row.isNewRevaluation ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        deleteNewRevaluation(holdingClassId);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  ) : null,
              },
            ];

            const isLast = index === entries.length - 1;

            if (simpleHeader) {
              return (
                <React.Fragment key={holdingClassId}>
                  <Column spacing="sm">
                    <Row justify="space-between" centered fullWidth>
                      <Typography variant="h6">
                        {holdingClassName ?? ''}
                      </Typography>
                      <ValueRow spacing="sm">
                        <Typography variant="body2">
                          {numberToDisplayString(currentNumberOfUnits)} units
                        </Typography>
                        <Typography variant="body2">
                          {numberToCurrencyString(currentHoldValue, {
                            currency,
                          })}{' '}
                          value
                        </Typography>
                      </ValueRow>
                    </Row>
                    <TransparentTableWrapper>
                      <Table scrollable columns={columns} rows={rows} />
                    </TransparentTableWrapper>
                    {!shouldRenderNewRevaluation(holdingClassId) ? (
                      <Row justify="flex-end">
                        <Button
                          color="primary"
                          variant="outlined"
                          onClick={() => {
                            addNewRevaluation(holdingClassId);
                          }}
                          size="large"
                        >
                          Add new revaluation
                        </Button>
                      </Row>
                    ) : null}
                  </Column>
                  {!isLast && <StackedDivider />}
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={holdingClassId}>
                <AccordionComponent defaultExpanded disableGutters>
                  <AccordionSummary
                    expandIcon={<ExpandMore fontSize="small" />}
                  >
                    <Row justify="space-between" centered fullWidth>
                      <Typography variant="h5">
                        <ColorIndicatorWithText
                          colorKey={{ holdingClassName }}
                          text={holdingClassName ?? ''}
                        />
                      </Typography>
                      <ValueRow spacing="sm">
                        <Typography variant="body2">
                          {numberToDisplayString(currentNumberOfUnits)} units
                        </Typography>
                        <Typography variant="body2">
                          {numberToCurrencyString(currentHoldValue, {
                            currency,
                          })}{' '}
                          value
                        </Typography>
                      </ValueRow>
                    </Row>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Column spacing="sm">
                      <Table scrollable columns={columns} rows={rows} />
                      {!shouldRenderNewRevaluation(holdingClassId) ? (
                        <Row justify="flex-end">
                          <Button
                            color="primary"
                            variant="outlined"
                            onClick={() => {
                              addNewRevaluation(holdingClassId);
                            }}
                            size="large"
                          >
                            Add new revaluation
                          </Button>
                        </Row>
                      ) : null}
                    </Column>
                  </AccordionDetails>
                </AccordionComponent>
              </React.Fragment>
            );
          },
        )}
      </Column>
    </ErrorBoundary>
  );
};

export default HoldingClassRevaluationSection;
